import { CheckIcon, Plus, SparklesIcon } from 'assets/icons';
import Button from 'components/button';
import { taskTypeLabel, taskTypeOf } from 'constants/taskTypes';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { addDays, dayLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const MIN_NOTE_LENGTH = 20;
const DEBOUNCE_MS = 1200;
const OBJECT_ID = /^[0-9a-f]{24}$/i;

/**
 * « Tâches proposées »: up to 3 tasks the AI draws from a note, each created
 * with one click. Nothing shows without a proposal, and an AI failure is silent.
 */
const TaskSuggestions = ({
  note = '',
  origin,
  companyId,
  groupId,
  contactName,
  contactRole,
  contactPhone,
  contactEmail,
  companyPhone,
  companyEmail,
  enabled,
}) => {
  const { language, text } = useText('taskSuggestions');
  const { text: formText } = useText('taskForm');
  const { text: tasksText } = useText('tasks');
  const { call } = useCrmContext();
  const notify = useNotifyTasksChanged();
  const [tasks, setTasks] = useState([]);
  const [reading, setReading] = useState(false);
  const [created, setCreated] = useState({});
  const [creating, setCreating] = useState({});
  const lastAsked = useRef(null);
  // Only the latest request may update the box: an answer to an older note is
  // dropped, and the newer request ends the reading state.
  const requestSeq = useRef(0);

  const targetId = [companyId, groupId].find(
    (id) => typeof id === 'string' && OBJECT_ID.test(id),
  );
  // Unlike useDebouncedValue, starts empty: a note present at mount also waits.
  const [debouncedNote, setDebouncedNote] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedNote(note), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [note]);
  const trimmed = debouncedNote.trim();
  const eligible = Boolean(
    enabled && targetId && trimmed.length >= MIN_NOTE_LENGTH,
  );

  useEffect(() => {
    if (!eligible) {
      requestSeq.current += 1;
      setTasks([]);
      setReading(false);
      lastAsked.current = null;
      return;
    }
    if (lastAsked.current === debouncedNote) return;
    lastAsked.current = debouncedNote;
    const seq = ++requestSeq.current;
    setReading(true);
    (async () => {
      try {
        const answer = await call(crmApi.suggestTasks, {
          note: debouncedNote,
          origin,
          companyId: OBJECT_ID.test(companyId ?? '') ? companyId : undefined,
          groupId: OBJECT_ID.test(groupId ?? '') ? groupId : undefined,
          contactName: contactName || undefined,
        });
        if (requestSeq.current !== seq) return;
        setTasks(Array.isArray(answer?.tasks) ? answer.tasks : []);
        setCreated({});
        setCreating({});
      } catch {
        if (requestSeq.current === seq) setTasks([]);
      } finally {
        if (requestSeq.current === seq) setReading(false);
      }
    })();
    // Only a new note asks the AI again: a change of company or contact alone
    // must not spend another call.
  }, [eligible, debouncedNote]);

  if (!eligible || (!reading && tasks.length === 0)) return null;

  const dueText = (dueOn) => {
    const today = new Date();
    if (dueOn === isoDay(today)) return text('today');
    if (dueOn === isoDay(addDays(today, 1))) return text('tomorrow');
    return dayLabel(fromIsoDay(dueOn));
  };

  const contactDetailOf = (type) => {
    if (type === 'call') return contactPhone || companyPhone || '';
    if (type === 'email') return contactEmail || companyEmail || '';
    return '';
  };

  const toastText = (task) => {
    const today = isoDay(new Date());
    const when =
      task.dueOn === today
        ? ''
        : task.dueOn === isoDay(addDays(new Date(), 1))
          ? formText('whenTomorrow')
          : formText('whenDay', {
              day: dayLabel(fromIsoDay(task.dueOn)).toLowerCase(),
            });
    return `${formText(task.type === 'call' ? 'addedCall' : 'addedTodo')}${when}`;
  };

  const create = async (task, index) => {
    setCreating((current) => ({ ...current, [index]: true }));
    try {
      await call(crmApi.createTask, {
        fields: {
          type: task.type,
          title: task.title,
          dueOn: task.dueOn,
          note,
          ...(OBJECT_ID.test(companyId ?? '') ? { companyId } : { groupId }),
          contactName,
          contactRole,
          contactDetail: contactDetailOf(task.type),
        },
      });
      setCreated((current) => ({ ...current, [index]: true }));
      toast.success(toastText(task));
      notify();
    } catch (error) {
      const message = crmTaskErrorText(error, tasksText);
      if (message) toast.error(message);
    } finally {
      setCreating((current) => ({ ...current, [index]: false }));
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
        {text('title')}
        <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
          <SparklesIcon wh={12} color="currentColor" />
          {text('badge')}
        </span>
      </p>
      {reading ? (
        <p className="text-xs text-gray-500">{text('reading')}</p>
      ) : (
        tasks.map((task, index) => {
          const Icon = taskTypeOf(task.type).icon;
          return (
            <div
              key={`${task.title}-${index}`}
              className="flex items-center gap-3"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-900">
                <Icon wh={16} color="currentColor" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-medium text-gray-900">
                  {task.title}
                </span>
                <span className="truncate text-xs text-gray-500">
                  {text('line', {
                    type: taskTypeLabel(language, task.type),
                    due: dueText(task.dueOn),
                  })}
                </span>
              </span>
              {created[index] ? (
                <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-green-600">
                  <CheckIcon w={14} h={11} color="currentColor" />
                  {text('created')}
                </span>
              ) : (
                <Button
                  color={creating[index] ? 'DISABLED' : 'SECONDARY'}
                  size="SM"
                  disabled={Boolean(creating[index])}
                  onClick={() => create(task, index)}
                >
                  <Plus wh={16} color="currentColor" />
                  {text('create')}
                </Button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default TaskSuggestions;
